import { fetchProject } from 'features/BillOfMaterials/store/asyncActions/fetchProject'
import { newProjectActions } from 'features/BillOfMaterials/store/newProjectReducer'
import {
  AdditionalWorkingStepFormConfiguration,
  AdditionalWorkingStepFormModelBuilder,
} from 'model/AdditionalWorkingStep/AdditionalWorkingStepForm'
import {
  AdditionalWorkingStepDto,
  CopyWorkingStepArgs,
  ResourceWorkingStepManualDiscountArgs,
  ResourceWorkingStepManualSurchargeArgs,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { store } from 'store/configureStore'
import { ProjectState } from 'store/Project/ProjectTypes'
import { FormDataDto } from '../model/FormDataDto'
import {
  WorkingStepDto,
  WorkingStepsDto,
} from '../services/APIs/InternalAPI/internal-api.contracts'
import { WorkingStepsAPI } from '../services/APIs/InternalAPI/WorkingStepsAPI'
import { APIFactory, BaseController } from './BaseController'

export interface IWorkingStepsAPI {
  GetWorkingSteps: (
    projectId: string,
    rowId: string
  ) => Promise<WorkingStepsDto>
  SaveWorkingStepForm: (
    projectId: string,
    rowIds: string[],
    workingStepDto: WorkingStepDto,
    formData: FormDataDto
  ) => Promise<void>
  DeleteAdditionalWorkingStep: (
    projectId: string,
    rowIds: string[],
    workingStepDto: WorkingStepDto
  ) => Promise<void>
  GetWorkingStepForm: (
    projectId: string,
    bomItemId: string,
    workingStepType: WorkingStepType
  ) => Promise<AdditionalWorkingStepDto>
  DeleteWorkingSteps: (
    projectId: string,
    rowIds: string[],
    primaryWorkingStepsToDelete: WorkingStepType[]
  ) => Promise<void>
  SetWorkingStepManualDiscount: (
    projectId: string,
    workingStepType: WorkingStepType,
    args: ResourceWorkingStepManualDiscountArgs
  ) => Promise<void>
  SetWorkingStepManualSurcharge: (
    projectId: string,
    workingStepType: WorkingStepType,
    args: ResourceWorkingStepManualSurchargeArgs
  ) => Promise<void>
  DeleteWorkingStepManualDiscount: (
    projectId: string,
    workingStepType: WorkingStepType
  ) => Promise<void>
  DeleteWorkingStepManualSurcharge: (
    projectId: string,
    workingStepType: WorkingStepType
  ) => Promise<void>
  CopyWorkingStepsToOtherParts: (
    projectId: string,
    args: CopyWorkingStepArgs
  ) => Promise<void>
}

export class WorkingStepsController extends BaseController<IWorkingStepsAPI> {
  static WorkingStepsOperations = {
    LOAD_DATA: 'WORKING_STEPS_LOAD_DATA',
    SAVE_DATA: 'WORKING_STEPS_SAVE_DATA',
    DELETE_DATA: 'WORKING_STEPS_DELETE_DATA',
    GET_FORM: 'WORKING_STEPS_GET_FORM',
  }

  private projectId: string

  constructor(partyId?: string, apiFactory?: APIFactory<IWorkingStepsAPI>) {
    super(
      apiFactory
        ? apiFactory
        : (onRequestChange) =>
            new WorkingStepsAPI(
              partyId || store.getState().user.organizationContext.id,
              onRequestChange
            )
    )

    this.projectId = store.getState().project?.activeProject?.id
  }

  public async DeleteWorkingSteps(
    projectId: string,
    rowIds: string[],
    workingStepsToRemove: WorkingStepDto[]
  ) {
    try {
      await this.api.DeleteWorkingSteps(
        projectId,
        rowIds,
        workingStepsToRemove.map((x) => x.primaryWorkingStep)
      )
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  async DeleteAdditionalWorkingStepData(
    projectId: string,
    rowIds: string[],
    data: WorkingStepDto[]
  ) {
    try {
      await Promise.all(
        data.map((x) =>
          this.api.DeleteAdditionalWorkingStep(projectId, rowIds, x)
        )
      )
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }

  public async SaveAdditionalWorkingSteps(
    projectId: string,
    rowIds: string[],
    data: {
      workingStep: WorkingStepDto
      formData: FormDataDto
    }[]
  ) {
    try {
      await Promise.all(
        data.map((x) =>
          this.api.SaveWorkingStepForm(
            projectId,
            rowIds,
            x.workingStep,
            x.formData
          )
        )
      )
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }

  async GetWorkingSteps(projectId: string, bomItemId: string) {
    try {
      return await this.api.GetWorkingSteps(projectId, bomItemId)
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }

  async GetWorkingStepForm(
    projectId: string,
    bomItemId: string,
    workingStepType: WorkingStepType
  ): Promise<AdditionalWorkingStepFormConfiguration> {
    try {
      const resp = await this.api.GetWorkingStepForm(
        projectId,
        bomItemId,
        workingStepType
      )

      return AdditionalWorkingStepFormModelBuilder().FromAPIResponse(resp)
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }

  public async handleSaveAndRemoveWorkingSteps(
    rowIds: string[],
    additionalWorkingStepsData: {
      workingStep: WorkingStepDto
      formData: FormDataDto
      isEmpty: boolean
      costDriveForm: AdditionalWorkingStepDto
    }[],
    workingStepsToRemove: WorkingStepDto[]
  ) {
    try {
      workingStepsToRemove?.forEach((ws) => {
        rowIds.forEach((rowId) => {
          store.dispatch(
            newProjectActions.removeWorkingStep({
              bomItemId: rowId,
              workingStepType: ws.primaryWorkingStep,
            })
          )
        })
      })

      additionalWorkingStepsData?.forEach((data) => {
        rowIds.forEach((rowId) => {
          store.dispatch(
            newProjectActions.addWorkingStep({
              bomItemId: rowId,
              workingStep: data.workingStep,
            })
          )
        })
      })

      store.dispatch(newProjectActions.refreshWorkingStepFilter())

      await this.DeleteWorkingSteps(
        this.projectId,
        rowIds,
        workingStepsToRemove
      )

      await this.SaveAdditionalWorkingSteps(
        this.projectId,
        rowIds,
        additionalWorkingStepsData
      )
    } catch (err) {
      this.LogError(err)
      store.dispatch(fetchProject({ projectId: this.projectId }))
      throw err
    }
  }

  public async SetWorkingStepManualDiscount(
    workingStepKey: string,
    workingStepType: WorkingStepType,
    args: ResourceWorkingStepManualDiscountArgs
  ) {
    try {
      store.dispatch(
        newProjectActions.setProjectStateProperties({
          priceSummaries: {
            [workingStepKey]: {
              discountRatio: args.discountRatio,
              discountValue: args.discountValue,
              totalSalesPrice: { value: null },
            },
          },
        } as ProjectState)
      )

      return await this.api.SetWorkingStepManualDiscount(
        this.projectId,
        workingStepType,
        args
      )
    } catch (err) {
      this.LogError(err)
      store.dispatch(fetchProject({ projectId: this.projectId }))
      throw err
    }
  }

  public async SetWorkingStepManualSurcharge(
    workingStepKey: string,
    workingStepType: WorkingStepType,
    args: ResourceWorkingStepManualSurchargeArgs
  ) {
    try {
      store.dispatch(
        newProjectActions.setProjectStateProperties({
          priceSummaries: {
            [workingStepKey]: {
              discountRatio: args.surchargeRatio,
              discountValue: args.surchargeValue,
              totalSalesPrice: { value: null },
            },
          },
        } as ProjectState)
      )

      return await this.api.SetWorkingStepManualSurcharge(
        this.projectId,
        workingStepType,
        args
      )
    } catch (err) {
      this.LogError(err)
      store.dispatch(fetchProject({ projectId: this.projectId }))

      throw err
    }
  }

  public async DeleteWorkingStepManualDiscount(
    workingStepType: WorkingStepType
  ) {
    try {
      return await this.api.DeleteWorkingStepManualDiscount(
        this.projectId,
        workingStepType
      )
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }

  public async DeleteWorkingStepManualSurcharge(
    workingStepType: WorkingStepType
  ) {
    try {
      return await this.api.DeleteWorkingStepManualSurcharge(
        this.projectId,
        workingStepType
      )
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }

  public async CopyWorkingStepsToOtherParts(
    baseBomItemId: string,
    workingStepType: WorkingStepType,
    bomItemsToApply?: string[]
  ) {
    try {
      return await this.api.CopyWorkingStepsToOtherParts(this.projectId, {
        sourcePartTypeId: baseBomItemId,
        workingStepType,
        destinationPartTypeIds: bomItemsToApply,
      })
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }
}
